import * as THREE from 'three';
import { createScene } from './core/threeCoreHandler';
import { createRenderer } from './core/threeCoreHandler';
import { createOrthoCamera } from './core/threeCoreHandler';
import { createRaycaster } from './utils/createRaycaster';
import { RipplePlane } from './world/RipplePlane';

export class WebGlApp {
  // Init
  private scene: THREE.Scene;
  private camera: THREE.OrthographicCamera;
  private renderer: THREE.WebGLRenderer;
  private raycaster: THREE.Raycaster;
  private canvasWidth: number;
  private canvasHeight: number;
  private canvasPixelRatio: number;
  // Custom
  private ripple: RipplePlane;
  private readonly canvas: HTMLCanvasElement;
  private resizeObserver: ResizeObserver | null = null;
  private resizeQueued: boolean = false;
  private resizeRafId: number = 0;
  private pointer: THREE.Vector2 = new THREE.Vector2();
  private mobileQuery: MediaQueryList;
  private sourceImageAspect: number | null = null;
  private sourceImageSrc: string | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.mobileQuery = window.matchMedia('(max-width: 767px)');
    this.raycaster = createRaycaster();
    this.canvasWidth = canvas.getBoundingClientRect().width;
    this.canvasHeight = canvas.getBoundingClientRect().height;
    this.canvasPixelRatio = Math.min(window.devicePixelRatio, 2);

    if (typeof this.mobileQuery.addEventListener === 'function') {
      this.mobileQuery.addEventListener('change', this.handleBreakpointChange);
    } else if (typeof this.mobileQuery.addListener === 'function') {
      this.mobileQuery.addListener(this.handleBreakpointChange);
    }

    this.sourceImageSrc = this.resolveImageSource();

    // Three.js setup
    this.scene = createScene();
    this.camera = createOrthoCamera(this.canvasWidth, this.canvasHeight);
    this.renderer = createRenderer(canvas);
    this.renderer.setSize(this.canvasWidth, this.canvasHeight, false);
    this.renderer.setPixelRatio(this.canvasPixelRatio);

    // Custom
    this.ripple = new RipplePlane(
      this.canvasWidth,
      this.canvasHeight,
      this.sourceImageSrc ?? canvas.dataset.imgSrc ?? ''
    );
    this.scene.add(this.ripple.getInstance());

    this.preloadSourceImage();

    // Events: respond to both window and element size changes
    window.addEventListener('resize', this.queueResize);
    this.attachResizeObserver();

    // Ensure initial sizing is correct
    this.applyImageConstrainedSize();
    this.onResize();

    // WebGL Setupが走ったことをLoading処理に伝える
    window.dispatchEvent(new Event('webglSetup'));
    this.canvas.style.opacity = '1';
    this.canvas.addEventListener('pointerdown', this.handlePointerDown);
  }

  // ------------------------ Public ------------------------
  /**
   * AppHandlerによってRAFごとに呼び出される関数
   */
  public render(): void {
    const elapsedTime = performance.now();
    this.update(elapsedTime);
    this.renderer.render(this.scene, this.camera);
  }
  public update(elapsedTime: number): void {
    this.ripple.update(elapsedTime);
  }


  // ------------------------ Private ------------------------
  // ブラウザのウィンドウサイズが変更されたときに実行
  private onResize = (): void => {
    this.applyImageConstrainedSize();

    const rect = this.canvas.getBoundingClientRect();
    const newWidth = rect.width;
    const newHeight = rect.height;
    const newPixelRatio = Math.min(window.devicePixelRatio, 2);

    // Skip if nothing effectively changed
    if (
      Math.abs(newWidth - this.canvasWidth) < 1 &&
      Math.abs(newHeight - this.canvasHeight) < 1 &&
      newPixelRatio === this.canvasPixelRatio
    ) {
      return;
    }

    this.canvasWidth = newWidth;
    this.canvasHeight = newHeight;
    this.canvasPixelRatio = newPixelRatio;

    // Update orthographic frustum to match canvas size
    this.camera.left = -this.canvasWidth * 0.5;
    this.camera.right = this.canvasWidth * 0.5;
    this.camera.top = this.canvasHeight * 0.5;
    this.camera.bottom = -this.canvasHeight * 0.5;
    this.camera.updateProjectionMatrix();

    // Update renderer (do not override CSS styles)
    this.renderer.setSize(this.canvasWidth, this.canvasHeight, false);
    this.renderer.setPixelRatio(this.canvasPixelRatio);
    this.ripple.resize(this.canvasWidth, this.canvasHeight);
  }

  private attachResizeObserver = () => {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => {
      this.queueResize();
    });
    this.resizeObserver.observe(this.canvas);
  }

  private queueResize = () => {
    if (this.resizeQueued) return;
    this.resizeQueued = true;
    this.resizeRafId = requestAnimationFrame(() => {
      this.resizeQueued = false;
      this.onResize();
    });
  }
  private handlePointerDown = (event: PointerEvent) => {
    const rect = this.canvas.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.pointer.set(x, y);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const intersection = this.raycaster.intersectObject(this.ripple.getInstance());
    if (intersection.length > 0 && intersection[0].uv) {
      this.ripple.triggerRipple(intersection[0].uv.clone());
    }
  }

  private handleBreakpointChange = () => {
    const nextSrc = this.resolveImageSource();
    if (nextSrc && nextSrc !== this.sourceImageSrc) {
      this.sourceImageSrc = nextSrc;
      this.sourceImageAspect = null;
      this.ripple.updateTexture(nextSrc);
      this.preloadSourceImage();
    } else if (!nextSrc) {
      this.sourceImageSrc = null;
      this.sourceImageAspect = null;
      this.applyImageConstrainedSize();
      this.queueResize();
    } else {
      this.applyImageConstrainedSize();
      this.queueResize();
    }
  }

  private resolveImageSource(): string | null {
    const mobileSrc = this.canvas.dataset.mobileImgSrc;
    const defaultSrc = this.canvas.dataset.imgSrc;
    return this.mobileQuery.matches ? (mobileSrc ?? defaultSrc ?? null) : (defaultSrc ?? null);
  }

  private preloadSourceImage(): void {
    if (!this.sourceImageSrc) return;
    this.sourceImageAspect = null;
    const image = new Image();
    image.src = this.sourceImageSrc;

    const finalize = () => {
      if (image.naturalWidth && image.naturalHeight) {
        this.sourceImageAspect = image.naturalWidth / image.naturalHeight;
        this.applyImageConstrainedSize();
        this.queueResize();
      }
    };

    image.onload = finalize;
    image.onerror = () => { /* ignore */ };

    if ('decode' in image) {
      image.decode().then(finalize).catch(finalize);
    }

    if (image.complete) {
      finalize();
    }
  }

  private applyImageConstrainedSize(): void {
    if (!this.sourceImageAspect) {
      this.canvas.style.height = '';
      return;
    }

    if (!this.mobileQuery.matches) {
      this.canvas.style.height = '';
      return;
    }

    const parent = this.canvas.parentElement;
    const parentWidth = parent ? parent.getBoundingClientRect().width : this.canvas.getBoundingClientRect().width;
    if (parentWidth <= 0) return;

    const targetHeight = parentWidth / this.sourceImageAspect;
    this.canvas.style.height = `${targetHeight}px`;
  }
}
